import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { every, isEmpty } from 'underscore';
import SteppedModal from 'components/SteppedModal';
import BlueskyPostModal, {
  useSwitchAccountStep,
} from 'containers/BlueskyPostModal';
import {
  ACCESS_DENIED_MESSAGE,
  ACCESS_DENIED_STEP,
  AUTHORIZATION_STEP,
} from 'containers/BlueskyPostModal/constants';
import useAuthenticationProgressStep from 'containers/BlueskyPostModal/steps/useAuthenticationProgressStep';
import useAuthorizationStep from 'containers/BlueskyPostModal/steps/useAuthorizationStep';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import useBlueskyAuthenticator from 'hooks/useBlueskyAuthenticator';
import { showError } from 'redux/modules/notification';
import {
  blueskyUserDataSelector,
  isLoadingBlueskyUserSelector,
} from 'redux/modules/social';

import { useAutopostingComposerStep } from './steps';
import {
  BlueskyAutoPostModalSteps,
  BlueskySheetDefaultValues,
  UseComposerStepValue,
} from './types';
import { block } from './utils';

const BlueskyAutoPostModal: React.FC = () => {
  const [activeStep, setActiveStep] = useState('');

  const modalRef = useRef<SteppedModal>(null);

  const { params, show, onExited, onHide } = useConnectedModal(
    'BlueskyAutoPost',
  );

  const blueskyUserData = useSelector(blueskyUserDataSelector);
  const isLoadingBlueskyUser = useSelector(isLoadingBlueskyUserSelector);

  const dispatch = useDispatch();

  const isComposerStep = ['composer', 'additional-details'].includes(
    activeStep,
  );

  const { subscriptionItem, appRedirectUrl } = params ?? {};

  const { description, allowQuotePost, replyLevel, customReplyAudiences } =
    subscriptionItem?.autoPostVideoPreference.options || {};

  const initialValues = React.useMemo((): BlueskySheetDefaultValues => {
    return {
      description:
        description ?? '<Episode Description>\nEpisode link: <Link To Episode>',
      allowQuotePost: allowQuotePost ?? true,
      replyLevel: replyLevel ?? 'everybody',
      customReplyAudiences: customReplyAudiences ?? [],
    };
  }, [allowQuotePost, customReplyAudiences, description, replyLevel]);

  const replaceModalStep = useCallback(
    (id: BlueskyAutoPostModalSteps): void => {
      modalRef.current?.stepHistory.replace(id);
    },
    [],
  );

  const handleGoToComposerStep = useCallback(() => {
    if (every(blueskyUserData, isEmpty)) {
      return undefined;
    }

    return replaceModalStep('composer');
  }, [blueskyUserData, replaceModalStep]);

  const handleAuthorizationError = useCallback(
    (err: Error) => {
      if (err.message === ACCESS_DENIED_MESSAGE) {
        replaceModalStep(ACCESS_DENIED_STEP);
      } else {
        replaceModalStep(AUTHORIZATION_STEP);

        onHide();
        dispatch(showError('Error authenticating with Bluesky'));
      }
    },
    [dispatch, onHide, replaceModalStep],
  );

  const { authenticating, authenticate } = useBlueskyAuthenticator({
    force: true,
    appRedirectUrl,
    onAuthSuccess: () => replaceModalStep('composer'),
    onError: handleAuthorizationError,
  });

  const isAuthenticating = authenticating || isLoadingBlueskyUser;

  const handleHide = useCallback(
    (values?: UseComposerStepValue) => {
      handleGoToComposerStep();
      onHide(values);
    },
    [handleGoToComposerStep, onHide],
  );

  const handleAuthenticate = useCallback((): void => {
    replaceModalStep('authentication-in-progress');
    authenticate();
  }, [authenticate, replaceModalStep]);

  const authorization = useAuthorizationStep({
    isAuthenticating,
    onAuthenticate: handleAuthenticate,
  });

  const authenticationProgress = useAuthenticationProgressStep();

  const composerSteps = useAutopostingComposerStep({
    initialValues,
    onHideModal: handleHide,
    onSubmit: handleHide,
    onSwitchAccount: () => replaceModalStep('switch'),
    onAdditionalDetailsClick: () => replaceModalStep('additional-details'),
    onGoToPostComposerStep: () => replaceModalStep('composer'),
  });

  const switchAccount = useSwitchAccountStep({
    isAuthenticating,
    onReauthenticate: handleAuthenticate,
    onCancel: () => replaceModalStep('composer'),
  });

  return (
    <BlueskyPostModal
      {...{ show, onExited }}
      ref={modalRef}
      title={!isComposerStep && 'auto-post videos'}
      defaultStep={
        isAuthenticating ? authenticationProgress.id : authorization.id
      }
      steps={[
        authorization,
        authenticationProgress,
        switchAccount,
        ...composerSteps,
      ]}
      className={block({ 'composer-step-container': isComposerStep })}
      onStepChange={setActiveStep}
      onHide={() => handleHide()}
    />
  );
};

export default BlueskyAutoPostModal;
